import { RECOVERY_BLOCKS } from 'components/play/recovery';
import { parseJsonArray } from 'common/utils/graphqlJson';

/**
 * OTRIX Play — normalización de prácticas de recuperación a una forma ÚNICA para el
 * detalle y el runner, venga del backend (playRecoveryPractices) o del fallback local
 * (RECOVERY_BLOCKS, mientras dev no tenga la migración/seed corridos).
 *
 * Forma normalizada:
 * { id?, key?, title, description, category, icon, minutes, pattern?, video_url?,
 *   video_start_ms?, video_end_ms?, video_duration_ms?, video_has_audio,
 *   intro_video_url?, intro_duration_ms?, intro_ready_ms?, intro_has_audio, intro_required,
 *   musicTrack?, musicAlbum?, steps: [{ id?, key?, title, description?, type,
 *   duration_seconds, video_url?, image_url?, video_start_ms?, video_end_ms?,
 *   video_duration_ms?, video_has_audio }], totalSeconds }
 */

const sumSteps = (steps) => steps.reduce((acc, s) => acc + (s.duration_seconds || 0), 0);

/** Título/desc de una práctica: las de sistema van por i18n, las custom en texto plano. */
const practiceTitle = (t, p) =>
  p.key ? t(`recovery.blocks.${p.key}.title`, { ns: 'play' }) : p.title || '';

const practiceDesc = (t, p) =>
  p.key ? t(`recovery.blocks.${p.key}.desc`, { ns: 'play' }) : p.description || '';

/** Título de un paso: sistema → i18n del bloque; rest → "Descanso"; custom → texto plano. */
const stepTitle = (t, practiceKey, step) => {
  if (step.type === 'rest') return t('recovery.steps.rest', { ns: 'play', defaultValue: 'Descanso' });
  if (step.key && practiceKey) return t(`recovery.blocks.${practiceKey}.steps.${step.key}`, { ns: 'play' });
  return step.title || '';
};

/** Normaliza una práctica del BACKEND. */
export const normalizePractice = (t, p) => {
  const steps = (p.steps || []).map((s) => ({
    id: s.id,
    key: s.key || null,
    title: stepTitle(t, p.key, s),
    description: s.description || '',
    type: s.type || 'exercise',
    duration_seconds: s.duration_seconds || 60,
    video_url: s.video_url || null,
    image_url: s.image_url || null,
    // Marcas de recorte en ms. Se arrastran TAL CUAL, incluido el 0: `|| null` convertiría un
    // "empieza en el milisegundo cero" en "sin recorte", que no es lo mismo.
    video_start_ms: s.video_start_ms ?? null,
    video_end_ms: s.video_end_ms ?? null,
    video_duration_ms: s.video_duration_ms ?? null,
    video_has_audio: Boolean(s.video_has_audio),
    // Campo JSON del backend: llega como STRING (ver common/utils/graphqlJson).
    pattern: parseJsonArray(s.pattern)
  }));

  // `breathing_pattern` es JSON: sin parsear, phaseAt() reventaba con
  // "pattern.reduce is not a function" al iniciar cualquier respiración.
  const pattern = parseJsonArray(p.breathing_pattern);

  return {
    id: p.id,
    key: p.key || null,
    title: practiceTitle(t, p),
    description: practiceDesc(t, p),
    category: p.category,
    icon: p.icon || null,
    minutes: p.minutes || 5,
    pattern: pattern.length > 0 ? pattern : null,
    video_url: p.video_url || null,
    video_start_ms: p.video_start_ms ?? null,
    video_end_ms: p.video_end_ms ?? null,
    video_duration_ms: p.video_duration_ms ?? null,
    video_has_audio: Boolean(p.video_has_audio),
    // Saludo previo a la práctica. `intro_required` decide si hay puerta o solo un video que se
    // puede saltar; sin `intro_video_url` no hay pantalla de intro y se entra directo.
    intro_video_url: p.intro_video_url || null,
    intro_start_ms: p.intro_start_ms ?? null,
    intro_end_ms: p.intro_end_ms ?? null,
    intro_duration_ms: p.intro_duration_ms ?? null,
    intro_ready_ms: p.intro_ready_ms ?? null,
    intro_has_audio: Boolean(p.intro_has_audio),
    intro_required: Boolean(p.intro_required),
    musicTrack: p.music_track || null,
    musicAlbum: p.music_album || null,
    steps,
    totalSeconds: steps.length > 0 ? sumSteps(steps) : Math.max(60, (p.minutes || 1) * 60)
  };
};

// Descansos sintéticos del fallback (mismos valores que el seed del backend).
const FALLBACK_REST = { yoga: 25, mobility: 20 };

/** Normaliza los bloques locales legacy (fallback sin backend). */
export const fallbackPractices = (t) =>
  RECOVERY_BLOCKS.map((block) => {
    const total = Math.max(60, block.minutes * 60);
    let steps = [];

    if (block.steps?.length) {
      const rest = FALLBACK_REST[block.key] || 25;
      const restTotal = (block.steps.length - 1) * rest;
      const per = Math.max(30, Math.round((total - restTotal) / block.steps.length));
      steps = block.steps.flatMap((key, i) => {
        const exercise = {
          key,
          title: t(`recovery.blocks.${block.key}.steps.${key}`, { ns: 'play' }),
          description: '',
          type: 'exercise',
          duration_seconds: per,
          video_url: null,
          image_url: null,
          video_start_ms: null,
          video_end_ms: null,
          video_duration_ms: null,
          video_has_audio: false
        };
        return i < block.steps.length - 1
          ? [exercise, { key: null, title: t('recovery.steps.rest', { ns: 'play', defaultValue: 'Descanso' }), description: '', type: 'rest', duration_seconds: rest, video_url: null, image_url: null }]
          : [exercise];
      });
    }

    return {
      id: null,
      key: block.key,
      title: t(`recovery.blocks.${block.key}.title`, { ns: 'play' }),
      description: t(`recovery.blocks.${block.key}.desc`, { ns: 'play' }),
      category: block.pattern ? 'breathing' : block.key,
      icon: block.icon,
      minutes: block.minutes,
      pattern: block.pattern || null,
      video_url: null,
      video_start_ms: null,
      video_end_ms: null,
      video_duration_ms: null,
      video_has_audio: false,
      intro_video_url: null,
      intro_start_ms: null,
      intro_end_ms: null,
      intro_duration_ms: null,
      intro_ready_ms: null,
      intro_has_audio: false,
      intro_required: false,
      musicTrack: null,
      steps,
      totalSeconds: steps.length > 0 ? sumSteps(steps) : total
    };
  });

/** Paso activo dado el tiempo transcurrido (para el modo guiado). */
export const stepAt = (steps, elapsedSeconds) => {
  let offset = elapsedSeconds;
  for (let i = 0; i < steps.length; i += 1) {
    if (offset < steps[i].duration_seconds) {
      return { index: i, step: steps[i], stepElapsed: offset };
    }
    offset -= steps[i].duration_seconds;
  }
  const lastIndex = steps.length - 1;
  return lastIndex >= 0
    ? { index: lastIndex, step: steps[lastIndex], stepElapsed: steps[lastIndex].duration_seconds }
    : null;
};

/** Pasos de EJERCICIO completados (los rest no cuentan como logro). */
export const completedExerciseSteps = (steps, elapsedSeconds) => {
  let offset = elapsedSeconds;
  let done = 0;
  for (let i = 0; i < steps.length; i += 1) {
    if (offset >= steps[i].duration_seconds) {
      if (steps[i].type !== 'rest') done += 1;
      offset -= steps[i].duration_seconds;
    } else {
      break;
    }
  }
  return done;
};

export const exerciseStepCount = (steps) => steps.filter((s) => s.type !== 'rest').length;
